import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Bell, Moon, Settings } from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import NotificationItem from './NotificationItem';
import NotificationSnoozeMenu from './NotificationSnoozeMenu';
import { notificationApi } from '../services/api';

/**
 * NotificationCenter Component (Issue #496, #823, #1109)
 *
 * Displays a bell icon with unread count badge and snooze indicator.
 * Clicking opens a dropdown panel showing recent notifications.
 * Supports marking notifications as read and "mark all as read".
 * Includes snooze menu for pausing notifications.
 */
export const NotificationCenter: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSnoozeMenuOpen, setIsSnoozeMenuOpen] = useState(false);
  const [isSnoozed, setIsSnoozed] = useState(false);
  const [snoozeUntil, setSnoozeUntil] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellButtonRef = useRef<HTMLButtonElement>(null);
  const snoozeButtonRef = useRef<HTMLButtonElement>(null);
  const snoozeMenuRef = useRef<HTMLDivElement>(null);
  const {
    notifications,
    unreadCount,
    totalCount,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    markAsRead,
    markAllAsRead,
    loadMore,
  } = useNotifications();

  // Load snooze status
  const loadSnoozeStatus = useCallback(async () => {
    try {
      const preferences = await notificationApi.getPreferences();
      const snoozeTimestamp = preferences.snooze_until;
      setSnoozeUntil(snoozeTimestamp);
      setIsSnoozed(snoozeTimestamp ? new Date(snoozeTimestamp) > new Date() : false);
    } catch (err) {
      console.error('Failed to load snooze status:', err);
    }
  }, []);

  // Load snooze status on mount
  useEffect(() => {
    loadSnoozeStatus();
  }, [loadSnoozeStatus]);

  // Auto-clear snooze indicator when snooze expires
  useEffect(() => {
    if (!snoozeUntil || !isSnoozed) return;

    const snoozeExpiry = new Date(snoozeUntil).getTime();
    const now = Date.now();
    const timeUntilExpiry = snoozeExpiry - now;

    // If already expired, clear immediately
    if (timeUntilExpiry <= 0) {
      setIsSnoozed(false);
      setSnoozeUntil(null);
      return;
    }

    // Set timer to clear when snooze expires
    const timer = setTimeout(() => {
      setIsSnoozed(false);
      setSnoozeUntil(null);
    }, timeUntilExpiry);

    return () => clearTimeout(timer);
  }, [snoozeUntil, isSnoozed]);

  // Close dropdown and snooze menu when clicking outside both
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(target);
      const clickedOutsideSnoozeMenu = snoozeMenuRef.current && !snoozeMenuRef.current.contains(target);

      // Close dropdown if clicked outside both dropdown and snooze menu
      if (clickedOutsideDropdown && clickedOutsideSnoozeMenu) {
        setIsOpen(false);
        setIsSnoozeMenuOpen(false);
      }
    };

    if (isOpen || isSnoozeMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isSnoozeMenuOpen]);

  // Close snooze menu when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setIsSnoozeMenuOpen(false);
    }
  }, [isOpen]);

  // Handle notification read
  const handleNotificationRead = async (notificationId: number) => {
    try {
      await markAsRead(notificationId);
      // Don't close dropdown, allow user to read more notifications
    } catch (err: unknown) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (err: unknown) {
      console.error('Failed to mark all notifications as read:', err);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell icon button */}
      <button
        ref={bellButtonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 hover:bg-gray-100 rounded-full transition-colors ${
          isSnoozed ? 'text-secondary' : 'text-gray-600 hover:text-gray-900'
        }`}
        aria-label={isSnoozed ? 'Notifications (snoozed)' : 'Notifications'}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls="notification-dropdown"
        data-testid="notification-bell-button"
      >
        <Bell className="w-6 h-6" />

        {/* Snooze indicator */}
        {isSnoozed && (
          <Moon
            className="absolute -bottom-1 -right-1 w-4 h-4 text-secondary bg-white rounded-full p-0.5"
            aria-label="Snoozed"
          />
        )}

        {/* Unread count badge */}
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full min-w-[20px]">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div
          id="notification-dropdown"
          data-testid="notification-dropdown"
          className={`absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 ${isSnoozeMenuOpen ? 'pointer-events-none' : ''}`}
          aria-hidden={isSnoozeMenuOpen}
          {...(isSnoozeMenuOpen && { inert: '' })}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {/* Snooze button */}
              <button
                ref={snoozeButtonRef}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsSnoozeMenuOpen(!isSnoozeMenuOpen);
                }}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                aria-label="Snooze notifications"
                aria-expanded={isSnoozeMenuOpen}
                aria-haspopup="dialog"
                title="Snooze notifications"
                data-testid="snooze-menu-button"
              >
                <Settings className="w-4 h-4" />
              </button>

              {/* Mark all as read */}
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-sm text-secondary hover:text-ink font-medium"
                >
                  Mark all as read
                </button>
              )}
            </div>
          </div>

          {/* Notifications list */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading && notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dusty-teal mx-auto"></div>
                <p className="mt-2">Loading notifications...</p>
              </div>
            ) : error ? (
              <div className="p-8 text-center text-red-500">
                <p>Failed to load notifications</p>
                <p className="text-sm text-gray-500 mt-1">{error.message}</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                <p className="font-medium">No notifications yet</p>
                <p className="text-sm mt-1">We'll notify you when something happens</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onRead={handleNotificationRead}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer with Load More and Close buttons (Issue #595) */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-gray-200">
              {/* Load More button */}
              {hasMore && (
                <div className="text-center mb-2">
                  <button
                    onClick={loadMore}
                    disabled={isLoadingMore}
                    className="text-sm text-secondary hover:text-ink font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    {isLoadingMore ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-dusty-teal"></div>
                        Loading more...
                      </span>
                    ) : (
                      `Load more (${totalCount - notifications.length} remaining)`
                    )}
                  </button>
                </div>
              )}
              {/* Close button */}
              <div className="text-center">
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Snooze Menu */}
      <NotificationSnoozeMenu
        isOpen={isSnoozeMenuOpen}
        onClose={(reason) => {
          setIsSnoozeMenuOpen(false);
          // Only close dropdown when backdrop is clicked (E2E expectation)
          // Keep dropdown open after snooze/resume actions or Escape key
          if (reason === 'backdrop') {
            setIsOpen(false);
          }
          loadSnoozeStatus(); // Refresh snooze status after menu closes
        }}
        anchorEl={snoozeButtonRef.current}
        menuRef={snoozeMenuRef}
      />
    </div>
  );
};

export default NotificationCenter;
